import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	MenuItem,
	MenuList,
	Paper,
	Snackbar,
	Typography,
	SnackbarCloseReason,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import { type SyntheticEvent, useCallback, useEffect, useState } from "react";
import { useSearchAssetResources } from "../hooks/useSearchAssetResources";
import { useWorkOrderModalsContext } from "../../context/work-order-modals.context";
import { WorkOrderDrawingsTable } from "./resources-tables/WorkOrderDrawings/WorkOrderDrawingsTable";
import { Alert } from "@mui/lab";
import { WorkOrderLOTOTable } from "./resources-tables/WorkOrderLOTO/WorkOrderLOTOTable";
import { AssetLOTOPicker } from "./resources-tables/WorkOrderLOTO/components/asset-LOTO/asset-LOTO-picker";
import { useSearchWorkOrderLOTO } from "./resources-tables/WorkOrderLOTO/hooks/useSearchWorkOrderLOTO";
import { WorkOrderAttachmentsTable } from "./resources-tables/WorkOrderAttachments/WorkOrderAttachmentsTable";
import { useSearchWorkOrderAttachments } from "./resources-tables/WorkOrderAttachments/hooks/useSearchWorkOrderAttachments";
import { AssetAttachmentPicker } from "./resources-tables/WorkOrderAttachments/components/asset-Attachments/asset-Attachments-picker";
import { WorkOrderPartsTable } from "./resources-tables/WorkOrderParts/WorkOrderPartsTable";
import { useSearchWorkOrderParts } from "./resources-tables/WorkOrderParts/hooks/useSearchWorkOrderParts";
import { AssetPartsPicker } from "./resources-tables/WorkOrderParts/components/asset-parts/asset-parts-picker";
import { ImportAssetResourcesDialog } from "./resources-tables/asset-resources-dialogs/import-asset-resources-dialog";
import { AlertCreatingLOTODialog } from "./resources-tables/WorkOrderLOTO/components/alert-creating-LOTO-dialog";
import { getCrafts } from "@/pages/task-list/repositorie/TaskListRepositorie";
import { get } from "lodash-es";
import type { Craft } from "../../models/Craft";
import { useSearchWorkOrderResources } from "../hooks/useSearchWorkOrderResources";
import { useSearchWorkOrderDrawings } from "./resources-tables/WorkOrderDrawings/hooks/useSearchWorkOrderDrawings";
import { AssetDrawingsPicker } from "./resources-tables/WorkOrderDrawings/components/asset-drawings/asset-drawings-picker";

const moment = getMomentTz();

const useStyles = makeStyles((theme) => ({
	root: {
		height: "100%",
	},
	resourcesList: {
		height: "100%",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
	},
	menuList: {
		flexGrow: 1,
		overflowY: "auto",
		padding: 0,
	},
	menuItem: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		minWidth: "250px",
	},
	menuItemSelected: {
		backgroundColor: "rgba(173, 216, 230, 0.2)",
	},
	menuItemContent: {
		width: "100%",
		padding: theme.spacing(2),
	},
	tableContainer: {
		height: "100%",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	drawingsMenuItem: {
		backgroundColor: "rgba(147, 112, 219, 0.1)",
	},
	attachmentsMenuItem: {
		backgroundColor: "rgba(255, 165, 0, 0.1)",
	},
	lotoMenuItem: {
		backgroundColor: "rgba(50, 205, 50, 0.1)",
	},
	partsMenuItem: {
		backgroundColor: "rgba(70, 130, 180, 0.1)",
	},
}));

export type ResourceType = "DRAWINGS" | "ATTACHMENTS" | "LOTO" | "PARTS";

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	AssetId: number | null;
	WorkOrderId: number | null;
	newMode?: boolean;
	initialResource?: ResourceType;
	WorkOrderName: string;
	Title: string;
	AssetName: string;
	TypeCode: string;
	CraftId?: number;
	OpenTimestamp?: Date;
	CloseTimestamp?: Date;
};

export const TableWorkOrdesResoures = ({
	show,
	onHide,
	AssetId,
	WorkOrderId,
	newMode,
	initialResource,
	Title,
	WorkOrderName,
	AssetName,
	TypeCode,
	CraftId,
	OpenTimestamp,
	CloseTimestamp,
}: Props) => {
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("xl");
	const classes = useStyles();

	const {
		state: {
			assetDrawings,
			assetAttachments,
			assetParts,
			assetLOTO,
			workOrderAttachmentsList,
			workOrderDrawingsList,
			workOrderLOTOList,
			workOrderPartsList,
			isCreatingLOTO,
		},
		actions: {
			setAssetAttachmentsList,
			setAssetDrawings,
			setAssetLOTOList,
			setAssetPartsList,
			setWorkOrderAttachmentsList,
			setWorkOrderDrawingsList,
			setWorkOrderLOTOList,
			setWorkOrderPartsList,
			setIsCreatingLOTO,
		},
	} = useWorkOrderModalsContext();

	const [isWorkOrderDrawingsLoading, setIsWorkOrderDrawingsLoading] =
		useState<boolean>(false);
	const [workOrderDrawingsError, setWorkOrderDrawingsError] =
		useState<string>("");
	const [isWorkOrderLOTOLoading, setIsWorkOrderLOTOLoading] =
		useState<boolean>(false);
	const [workOrderLOTOError, setWorkOrderLOTOError] = useState<string>("");
	const [isWorkOrderAttachmentsLoading, setIsWorkOrderAttachmentsLoading] =
		useState<boolean>(false);
	const [workOrderPartsLoading, setIsWorkOrderPartsLoading] =
		useState<boolean>(false);
	const [workOrderPartsError, setWorkOrderPartsError] = useState<string>("");
	const [workOrderAttachmentsError, setWorkOrderAttachmentsError] =
		useState<string>("");
	const [isLoadingResourcesList, setIsLoadingResourcesList] =
		useState<boolean>(false);
	const [errorResourcesList, setErrorResourcesList] = useState<string>("");
	const [assetResourcesMessage, setAssetResourcesMessage] = useState(false);
	const [craftName, setCraftName] = useState<string>("");

	const [selectedItem, setSelectedItem] = useState<string | null>(
		initialResource ? initialResource : "DRAWINGS",
	);

	const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
	const [showWorkOrderDrawingsTable, setShowWorkOrderDrawingsTable] =
		useState(false);
	const [showWorkOrderAttachmentsTable, setShowWorkOrderAttachmentsTable] =
		useState(false);
	const [showWorkOrderLOTOTable, setShowWorkOrderLOTOTable] = useState(false);
	const [showWorkOrderPartsTable, setShowWorkOrderPartsTable] = useState(false);
	const [showAssetDrawingsPicker, setShowAssetDrawingsPicker] = useState(false);
	const [showAssetAttachmentsPicker, setShowAssetAttachmentsPicker] =
		useState(false);
	const [showAssetLOTOPicker, setShowAssetLOTOPicker] = useState(false);
	const [showAssetPartsPicker, setShowAssetPartsPicker] = useState(false);
	const [isLoadingWorkOrderResourcesList, setIsLoadingWorkOrderResourcesList] =
		useState<boolean>(false);
	const [errorWorkOrderResourcesList, setErrorWorkOrderResourcesList] =
		useState<string>("");
	const [showImportDialog, setShowImportDialog] = useState(false);

	const getCraftName = useCallback(async (craftId: number) => {
		const res = await getCrafts();
		if (res.ok) {
			const craftsData = get(res, "data.tables[0].rows", []);
			const craft = craftsData.find((c: Craft) => c.CraftId === craftId);
			if (craft) {
				setCraftName(craft.CraftName);
			}
		}
	}, []);

	const searchWorkOrderDrawings = useSearchWorkOrderDrawings({
		WorkOrderId,
		setIsWorkOrderDrawingsLoading,
		setWorkOrderDrawingsError,
	});

	const searchWorkOrderLOTO = useSearchWorkOrderLOTO({
		WorkOrderId,
		setIsWorkOrderLOTOLoading,
		setWorkOrderLOTOError,
	});

	const searchWorkOrderAttachments = useSearchWorkOrderAttachments({
		WorkOrderId,
		setIsWorkOrderAttachmentsLoading,
		setWorkOrderAttachmentsError,
	});

	const searchWorkOrderParts = useSearchWorkOrderParts({
		WorkOrderId,
		setIsWorkOrderPartsLoading,
		setWorkOrderPartsError,
	});

	const searchAssetResources = useSearchAssetResources({
		AssetId,
		setAssetAttachmentsList,
		setAssetDrawings,
		setAssetLOTOList,
		setAssetPartsList,
		setErrorResourcesList,
		setIsLoadingResourcesList,
		setAssetResourcesMessage,
	});

	const searchWorkOrderResources = useSearchWorkOrderResources({
		WorkOrderId,
		setWorkOrderAttachmentsList,
		setWorkOrderDrawingsList,
		setWorkOrderLOTOList,
		setWorkOrderPartsList,
		setErrorWorkOrderResourcesList,
		setIsLoadingWorkOrderResourcesList,
	});

	const handleCloseResourcesMessage = (
		event: Event | React.SyntheticEvent,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setAssetResourcesMessage(false);
	};

	const handleCancel = () => {
		onHide(true);
		setWorkOrderDrawingsList([]);
		setWorkOrderLOTOList([]);
		setWorkOrderAttachmentsList([]);
		setWorkOrderPartsList([]);
	};

	const handleItemClick = (item: string) => {
		setSelectedItem(item);
		setShowWorkOrderAttachmentsTable(item === "ATTACHMENTS");
		setShowWorkOrderDrawingsTable(item === "DRAWINGS");
		setShowWorkOrderLOTOTable(item === "LOTO");
		setShowWorkOrderPartsTable(item === "PARTS");
	};

	const showPicker = (item: string) => {
		switch (item) {
			case "DRAWINGS":
				setShowAssetDrawingsPicker(true);
				break;
			case "ATTACHMENTS":
				setShowAssetAttachmentsPicker(true);
				break;
			case "LOTO":
				if (isCreatingLOTO) {
					setShowConfirmDialog(true);
				} else {
					setShowAssetLOTOPicker(true);
				}
				break;
			case "PARTS":
				setShowAssetPartsPicker(true);
				break;
			default:
				console.error("Unknown item type");
		}
	};

	const isButtonDisabled = () => {
		switch (selectedItem) {
			case "DRAWINGS":
				return assetDrawings.length === 0;
			case "ATTACHMENTS":
				return assetAttachments.length === 0;
			case "LOTO":
				return assetLOTO.length === 0;
			case "PARTS":
				return assetParts.length === 0;
			default:
				return true;
		}
	};

	useEffect(() => {
		if (show) {
			searchAssetResources();
			searchWorkOrderResources();
			setSelectedItem(initialResource ? initialResource : "DRAWINGS");
			setShowWorkOrderDrawingsTable(initialResource === "DRAWINGS");
			setShowWorkOrderAttachmentsTable(initialResource === "ATTACHMENTS");
			setShowWorkOrderLOTOTable(initialResource === "LOTO");
			setShowWorkOrderPartsTable(initialResource === "PARTS");
			if (!initialResource) setShowWorkOrderDrawingsTable(true);
		} else {
			setSelectedItem("DRAWINGS");
			setShowWorkOrderDrawingsTable(false);
			setShowWorkOrderAttachmentsTable(false);
			setShowWorkOrderLOTOTable(false);
			setShowWorkOrderPartsTable(false);
			handleCancel();
		}
	}, [show, initialResource]);

	useEffect(() => {
		if (CraftId && show) {
			getCraftName(CraftId);
		}
	}, [CraftId, show, getCraftName]);

	useEffect(() => {
		if (show && newMode) {
			setShowImportDialog(true);
		}
	}, [show, newMode]);

	return (
		<>
			<Dialog
				maxWidth={maxWidth}
				open={show}
				onClose={handleCancel}
				fullWidth={true}
				style={{ margin: "0", padding: 5, width: "100% !important" }}
				className={classes.root}
				PaperProps={{
					sx: {
						maxHeight: "95vh",
						height: "auto",
					},
				}}
			>
				<Grid2
					container
					justifyContent="center"
					style={{
						height: "auto",
						maxHeight: "95vh",
						paddingBottom: 0,
						display: "flex",
						alignItems: "center",
					}}
				>
					<Grid2
						style={{
							padding: "0 24px",
							height: "auto",
							minHeight: "8vh",
						}}
						size={{ md: 12, xs: 12 }}
					>
						<Typography
							variant="h5"
							style={{
								fontWeight: 600,
								paddingTop: 6,
							}}
						>
							WORK ORDER RESOURCES
						</Typography>

						<Typography
							style={{
								display: "flex",
								alignItems: "center",
								fontSize: "1.05rem",
								flexWrap: "wrap",
								gap: "8px",
								padding: "4px 0 0",
							}}
						>
							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Work Order:</span>
								<span style={{ marginLeft: "4px" }}>{WorkOrderName}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Title:</span>
								<span style={{ marginLeft: "4px" }}>{Title}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Asset:</span>
								<span style={{ marginLeft: "4px" }}>{AssetName}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Type:</span>
								<span style={{ marginLeft: "4px" }}>{TypeCode}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Craft:</span>
								<span style={{ marginLeft: "4px" }}>{craftName}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Opened:</span>
								<span style={{ marginLeft: "4px" }}>
									{moment(OpenTimestamp).format("MM/DD/YYYY hh:mm A")}
								</span>
							</div>

							{CloseTimestamp && (
								<>
									<div
										style={{
											display: "inline-flex",
											alignItems: "center",
											whiteSpace: "nowrap",
											margin: "4px 8px",
										}}
									>
										|
									</div>
									<div
										style={{
											display: "inline-flex",
											alignItems: "center",
											whiteSpace: "nowrap",
											margin: "4px 0",
										}}
									>
										<span style={{ fontWeight: 600 }}>Closed:</span>
										<span style={{ marginLeft: "4px" }}>
											{moment(CloseTimestamp).format("MM/DD/YYYY hh:mm A")}
										</span>
									</div>
								</>
							)}
						</Typography>
					</Grid2>

					<DialogContent
						dividers
						style={{ height: "66vh", paddingTop: "12px", overflowY: "hidden" }}
					>
						<Grid2
							container
							justifyContent="space-between"
							alignItems="stretch"
							className={classes.root}
							style={{ height: "100%" }}
						>
							<Grid2 style={{ height: "100%" }} size={{ md: 3, xs: 12 }}>
								<Paper elevation={1} className={classes.resourcesList}>
									<MenuList className={`mes-submenu ${classes.menuList}`}>
										<MenuItem
											className={`${classes.menuItem} ${
												classes.drawingsMenuItem
											} ${
												selectedItem === "DRAWINGS"
													? classes.menuItemSelected
													: ""
											}`}
											onClick={() => handleItemClick("DRAWINGS")}
										>
											<div className={classes.menuItemContent}>
												<Typography variant="h6">
													DOCUMENTS
													{isLoadingResourcesList ? (
														<CircularProgress
															size={20}
															style={{
																marginLeft: "8px",
																verticalAlign: "middle",
															}}
														/>
													) : (
														workOrderDrawingsList.length > 0 &&
														` (${workOrderDrawingsList.length})`
													)}
												</Typography>
											</div>
										</MenuItem>

										<MenuItem
											className={`${classes.menuItem} ${
												classes.attachmentsMenuItem
											} ${
												selectedItem === "ATTACHMENTS"
													? classes.menuItemSelected
													: ""
											}`}
											onClick={() => handleItemClick("ATTACHMENTS")}
										>
											<div className={classes.menuItemContent}>
												<Typography variant="h6">
													ATTACHMENTS
													{isLoadingResourcesList ? (
														<CircularProgress
															size={20}
															style={{
																marginLeft: "8px",
																verticalAlign: "middle",
															}}
														/>
													) : (
														workOrderAttachmentsList.length > 0 &&
														` (${workOrderAttachmentsList.length})`
													)}
												</Typography>
											</div>
										</MenuItem>

										<MenuItem
											className={`${classes.menuItem} ${classes.lotoMenuItem} ${
												selectedItem === "LOTO" ? classes.menuItemSelected : ""
											}`}
											onClick={() => handleItemClick("LOTO")}
										>
											<div className={classes.menuItemContent}>
												<Typography variant="h6">
													LOTO
													{isLoadingResourcesList ? (
														<CircularProgress
															size={20}
															style={{
																marginLeft: "8px",
																verticalAlign: "middle",
															}}
														/>
													) : (
														workOrderLOTOList.length > 0 &&
														` (${workOrderLOTOList.length})`
													)}
												</Typography>
											</div>
										</MenuItem>

										<MenuItem
											className={`${classes.menuItem} ${
												classes.partsMenuItem
											} ${
												selectedItem === "PARTS" ? classes.menuItemSelected : ""
											}`}
											onClick={() => handleItemClick("PARTS")}
										>
											<div className={classes.menuItemContent}>
												<Typography variant="h6">
													PARTS
													{isLoadingResourcesList ? (
														<CircularProgress
															size={20}
															style={{
																marginLeft: "8px",
																verticalAlign: "middle",
															}}
														/>
													) : (
														workOrderPartsList.length > 0 &&
														` (${workOrderPartsList.length})`
													)}
												</Typography>
											</div>
										</MenuItem>
									</MenuList>
								</Paper>
							</Grid2>

							<Grid2 style={{ height: "100%" }} size={{ md: 9, xs: 12 }}>
								<Paper elevation={1} className={classes.tableContainer}>
									<WorkOrderDrawingsTable
										AssetId={AssetId}
										newMode={newMode}
										WorkOrderId={WorkOrderId}
										searchAssetResources={searchAssetResources}
										show={showWorkOrderDrawingsTable}
										onHide={() => {
											setShowWorkOrderDrawingsTable(false);
										}}
										setShowAssetDrawingsPicker={setShowAssetDrawingsPicker}
										isLoadingResourcesList={isLoadingResourcesList}
										WorkOrderName={WorkOrderName}
										AssetName={AssetName}
										Title={Title}
									/>

									<WorkOrderLOTOTable
										AssetId={AssetId}
										newMode={newMode}
										WorkOrderId={WorkOrderId}
										searchAssetResources={searchAssetResources}
										show={showWorkOrderLOTOTable}
										onHide={() => {
											setShowWorkOrderLOTOTable(false);
										}}
										setShowAssetLOTOPicker={setShowAssetLOTOPicker}
										isLoadingResourcesList={isLoadingResourcesList}
										AssetName={AssetName as string}
										WorkOrderName={WorkOrderName as string}
									/>

									<WorkOrderAttachmentsTable
										AssetId={AssetId}
										newMode={newMode}
										WorkOrderId={WorkOrderId}
										searchAssetResources={searchAssetResources}
										show={showWorkOrderAttachmentsTable}
										onHide={() => {
											setShowWorkOrderAttachmentsTable(false);
										}}
										setShowAssetAttachmentsPicker={
											setShowAssetAttachmentsPicker
										}
										isLoadingResourcesList={isLoadingResourcesList}
										WorkOrderName={WorkOrderName}
										AssetName={AssetName}
									/>

									<WorkOrderPartsTable
										AssetId={AssetId}
										newMode={newMode}
										WorkOrderId={WorkOrderId}
										searchAssetResources={searchAssetResources}
										show={showWorkOrderPartsTable}
										onHide={() => {
											setShowWorkOrderPartsTable(false);
										}}
										AssetName={AssetName as string}
										WorkOrderName={WorkOrderName as string}
										setShowAssetPartsPicker={setShowAssetPartsPicker}
										isLoadingResourcesList={isLoadingResourcesList}
									/>
								</Paper>
							</Grid2>
						</Grid2>
					</DialogContent>

					<Grid2 size={{ md: 12, xs: 12 }}>
						<DialogActions
							sx={{
								padding: "0px 24px",
								marginTop: "16px",
								paddingBottom: "16px",
								display: "flex",
								alignItems: "center",
							}}
						>
							<Grid2
								container
								spacing={2}
								style={{ margin: 0, display: "flex", alignItems: "center" }}
							>
								<Grid2 size={{ md: 3, xs: 4 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={handleCancel}
									>
										Close
									</Button>
								</Grid2>
							</Grid2>
							{selectedItem && (
								<Grid2 style={{ paddingRight: "8px" }} size={{ md: 4, xs: 12 }}>
									<Button
										variant="contained"
										color="primary"
										onClick={() => showPicker(selectedItem)}
										fullWidth
										disabled={isButtonDisabled()}
										style={{ minWidth: 300, height: 40 }}
									>
										{selectedItem === "DRAWINGS" ? "DOCUMENTS" : selectedItem}{" "}
										from asset
									</Button>
								</Grid2>
							)}
						</DialogActions>
						<AssetDrawingsPicker
							show={showAssetDrawingsPicker}
							WorkOrderId={WorkOrderId}
							onHide={(shouldUpdate) => {
								if (shouldUpdate) {
									searchWorkOrderDrawings();
								}
								setShowAssetDrawingsPicker(false);
							}}
							WorkOrderName={WorkOrderName}
							AssetName={AssetName}
							Title={Title}
						/>
						<AssetLOTOPicker
							show={showAssetLOTOPicker}
							WorkOrderId={WorkOrderId}
							onHide={(shouldUpdate) => {
								if (shouldUpdate) {
									searchWorkOrderLOTO();
								}
								setShowAssetLOTOPicker(false);
							}}
							WorkOrderName={WorkOrderName}
							AssetName={AssetName}
							Title={Title}
						/>
						<AssetAttachmentPicker
							show={showAssetAttachmentsPicker}
							WorkOrderId={WorkOrderId}
							onHide={(shouldUpdate) => {
								if (shouldUpdate) {
									searchWorkOrderAttachments();
								}
								setShowAssetAttachmentsPicker(false);
							}}
							WorkOrderName={WorkOrderName}
							AssetName={AssetName}
							Title={Title}
						/>
						<AssetPartsPicker
							show={showAssetPartsPicker}
							WorkOrderId={WorkOrderId}
							onHide={(shouldUpdate) => {
								if (shouldUpdate) {
									searchWorkOrderParts();
									onHide(true);
								}
								setShowAssetPartsPicker(false);
							}}
							WorkOrderName={WorkOrderName}
							AssetName={AssetName}
							Title={Title}
						/>
						<AlertCreatingLOTODialog
							showConfirmDialog={showConfirmDialog}
							setShowConfirmDialog={setShowConfirmDialog}
							setIsCreatingLOTO={setIsCreatingLOTO}
							openPicker={setShowAssetLOTOPicker}
							WorkOrderId={WorkOrderId}
						/>
					</Grid2>

					<AssetDrawingsPicker
						show={showAssetDrawingsPicker}
						WorkOrderId={WorkOrderId}
						onHide={(shouldUpdate) => {
							if (shouldUpdate) {
								searchWorkOrderDrawings();
							}
							setShowAssetDrawingsPicker(false);
						}}
						WorkOrderName={WorkOrderName}
						AssetName={AssetName}
						Title={Title}
					/>

					<AssetLOTOPicker
						show={showAssetLOTOPicker}
						WorkOrderId={WorkOrderId}
						onHide={(shouldUpdate) => {
							if (shouldUpdate) {
								searchWorkOrderLOTO();
							}
							setShowAssetLOTOPicker(false);
						}}
						WorkOrderName={WorkOrderName}
						AssetName={AssetName}
						Title={Title}
					/>

					<AssetAttachmentPicker
						show={showAssetAttachmentsPicker}
						WorkOrderId={WorkOrderId}
						onHide={(shouldUpdate) => {
							if (shouldUpdate) {
								searchWorkOrderAttachments();
							}
							setShowAssetAttachmentsPicker(false);
						}}
						WorkOrderName={WorkOrderName}
						AssetName={AssetName}
						Title={Title}
					/>

					<AssetPartsPicker
						show={showAssetPartsPicker}
						WorkOrderId={WorkOrderId}
						onHide={(shouldUpdate) => {
							if (shouldUpdate) {
								searchWorkOrderParts();
							}
							setShowAssetPartsPicker(false);
						}}
						WorkOrderName={WorkOrderName}
						AssetName={AssetName}
						Title={Title}
					/>

					<AlertCreatingLOTODialog
						showConfirmDialog={showConfirmDialog}
						setShowConfirmDialog={setShowConfirmDialog}
						setIsCreatingLOTO={setIsCreatingLOTO}
						openPicker={setShowAssetLOTOPicker}
						WorkOrderId={WorkOrderId}
					/>

					<ImportAssetResourcesDialog
						open={showImportDialog}
						onClose={() => setShowImportDialog(false)}
						onOpenAssetPartsPicker={() => {
							setShowAssetPartsPicker(true);
						}}
						onOpenAssetLOTOPicker={() => {
							setShowAssetLOTOPicker(true);
						}}
						onOpenAssetAttachmentsPicker={() => {
							setShowAssetAttachmentsPicker(true);
						}}
						onOpenAssetDrawingsPicker={() => {
							setShowAssetDrawingsPicker(true);
						}}
					/>

					<ErrorModal
						error={errorResourcesList}
						onHide={() => setErrorResourcesList("")}
					/>
					<ErrorModal
						error={workOrderDrawingsError}
						onHide={() => setWorkOrderDrawingsError("")}
					/>
					<ErrorModal
						error={workOrderLOTOError}
						onHide={() => setWorkOrderLOTOError("")}
					/>
					<ErrorModal
						error={workOrderAttachmentsError}
						onHide={() => setWorkOrderAttachmentsError("")}
					/>
					<ErrorModal
						error={workOrderPartsError}
						onHide={() => setWorkOrderPartsError("")}
					/>

					<Snackbar
						open={assetResourcesMessage}
						autoHideDuration={2500}
						onClose={handleCloseResourcesMessage}
						anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
					>
						<Alert severity="warning" onClose={handleCloseResourcesMessage}>
							The asset has no assigned resources
						</Alert>
					</Snackbar>
				</Grid2>
			</Dialog>
		</>
	);
};
